import React, { Suspense, useEffect } from 'react';
import { lazyComRecarga as lazy, LimiteDeErro } from './components/FalhaCarregamento';
import { BrowserRouter, Routes, Route, Navigate, useLocation } from 'react-router-dom';
import { AuthProvider, useAuth } from './contexts/CockpitAuthContext';
import { useMarca, aplicarNoDocumento } from './lib/marcaCliente';
import { vigiarSemMarcal } from './lib/semMarcal';
import { ClienteAuthProvider, useClienteAuth } from './contexts/ClienteAuthContext';
import { ThemeProvider } from './contexts/ThemeContext';
import { UIProvider } from './components/ui';
import { LayoutEscritorio } from './components/Layout';
import Login from './pages/Login';

// Code splitting: páginas viram chunks separados carregados sob demanda.
// Reduz bundle inicial em ~60-70% (de ~2MB pra ~700KB). Cada rota visitada
// faz fetch do chunk dela. Login não é lazy porque é a primeira tela.
const Aprovacoes = lazy(() => import('./pages/escritorio/Aprovacoes'));
const UltimasEmitidas = lazy(() => import('./pages/escritorio/UltimasEmitidas'));
const ControleNotas = lazy(() => import('./pages/escritorio/ControleNotas'));
const ControleFiscalMensal = lazy(() => import('./pages/escritorio/ControleFiscalMensal'));
const EmitirNota = lazy(() => import('./pages/escritorio/EmitirNota'));
const RascunhoNF = lazy(() => import('./pages/escritorio/RascunhoNF'));
const BugReports = lazy(() => import('./pages/escritorio/BugReports'));
const GestaoClientes = lazy(() => import('./pages/escritorio/GestaoClientes'));
const Contratos = lazy(() => import('./pages/escritorio/Contratos'));
const EmitirNFEscritorio = lazy(() => import('./pages/escritorio/EmitirNFEscritorio'));
const EmitirLote = lazy(() => import('./pages/escritorio/EmitirLote'));
const EmitirDeclaracao = lazy(() => import('./pages/escritorio/EmitirDeclaracao'));
const EmitirCertidao = lazy(() => import('./pages/escritorio/EmitirCertidao'));
const NfeProduto = lazy(() => import('./pages/escritorio/NfeProduto'));
const NfeCredenciamento = lazy(() => import('./pages/escritorio/NfeCredenciamento'));
const CertidoesLista = lazy(() => import('./pages/escritorio/CertidoesLista'));
const AcessoEmpresas = lazy(() => import('./pages/escritorio/AcessoEmpresas'));
const Folha12m = lazy(() => import('./pages/escritorio/Folha12m'));
const SaudeCrons = lazy(() => import('./pages/escritorio/SaudeCrons'));
const Manutencao = lazy(() => import('./pages/escritorio/Manutencao'));
const Acessos = lazy(() => import('./pages/escritorio/Acessos'));
const PainelNivelon = lazy(() => import('./pages/escritorio/PainelNivelon'));
const ContasNivelon = lazy(() => import('./pages/escritorio/ContasNivelon'));
const ConsumoSerpro = lazy(() => import('./pages/escritorio/ConsumoSerpro'));
const ProcuracoesEcac = lazy(() => import('./pages/escritorio/ProcuracoesEcac'));
const MotorNf = lazy(() => import('./pages/escritorio/MotorNf'));
const MotorNfDiagnostico = lazy(() => import('./pages/escritorio/MotorNfDiagnostico'));
const AnaMetricas = lazy(() => import('./pages/escritorio/AnaMetricas'));
const ClienteEspelho = lazy(() => import('./pages/escritorio/ClienteEspelho'));
const Observabilidade = lazy(() => import('./pages/escritorio/Observabilidade'));
const ClienteErros = lazy(() => import('./pages/escritorio/ClienteErros'));
const GuiasDriveNomenclatura = lazy(() => import('./pages/escritorio/GuiasDriveNomenclatura'));
const CentralDocumentos = lazy(() => import('./pages/escritorio/CentralDocumentos'));
const WhatsAppPainel = lazy(() => import('./pages/escritorio/WhatsApp'));
const PrimeiroContatoPainel = lazy(() => import('./pages/escritorio/PrimeiroContato'));
const AtendimentoMovel = lazy(() => import('./pages/AtendimentoMovel'));
const Financeiro = lazy(() => import('./pages/escritorio/Financeiro'));
const ChamadosPainel = lazy(() => import('./pages/escritorio/Chamados'));
const Agenda = lazy(() => import('./pages/escritorio/Agenda'));
const ChatEquipe = lazy(() => import('./pages/escritorio/ChatEquipe'));
const TvAtendimento = lazy(() => import('./pages/escritorio/TvAtendimento'));
const TvEmpresas = lazy(() => import('./pages/escritorio/TvEmpresas'));
const CentralAtendimento = lazy(() => import('./pages/escritorio/CentralAtendimento'));
const TvPublica = lazy(() => import('./pages/TvPublica'));
const EnviarCertificado = lazy(() => import('./pages/EnviarCertificado'));
const EnviarSenha = lazy(() => import('./pages/EnviarSenha'));
const AvaliarAtendimento = lazy(() => import('./pages/AvaliarAtendimento'));
const CertificadoCodigo = lazy(() => import('./pages/CertificadoCodigo'));
const Cadastros = lazy(() => import('./pages/escritorio/Cadastros'));
const PendenciasCadastrais = lazy(() => import('./pages/escritorio/PendenciasCadastrais'));
const WhatsappNivelonConexao = lazy(() => import('./pages/escritorio/WhatsappNivelonConexao'));
const WhatsappImportar = lazy(() => import('./pages/escritorio/WhatsappImportar'));
const AvisosClientes = lazy(() => import('./pages/escritorio/AvisosClientes'));
const AvisoIos27 = lazy(() => import('./pages/escritorio/AvisoIos27'));
const PainelAna = lazy(() => import('./pages/escritorio/PainelAna'));
const AnaTriagem = lazy(() => import('./pages/escritorio/AnaTriagem'));
const FluxoAtendimento = lazy(() => import('./pages/escritorio/FluxoAtendimento'));
const AnaConhecimento = lazy(() => import('./pages/escritorio/AnaConhecimento'));
const AnaSimulacao = lazy(() => import('./pages/escritorio/AnaSimulacao'));
const AnaDesempenho = lazy(() => import('./pages/escritorio/AnaDesempenho'));
const JornadaClientes = lazy(() => import('./pages/escritorio/JornadaClientes'));
const AparelhosClientes = lazy(() => import('./pages/escritorio/AparelhosClientes'));
const Relatorios = lazy(() => import('./pages/escritorio/Relatorios'));
const TestarAgente = lazy(() => import('./pages/escritorio/TestarAgente'));
const LeticiaChamados = lazy(() => import('./pages/escritorio/LeticiaChamados'));
const FilaAprovacaoAna = lazy(() => import('./pages/escritorio/FilaAprovacaoAna'));
const CertificadoSerpro = lazy(() => import('./pages/escritorio/CertificadoSerpro'));
const IntegraContador = lazy(() => import('./pages/escritorio/IntegraContador'));
const ApiDominio = lazy(() => import('./pages/escritorio/ApiDominio'));
const Apuracao = lazy(() => import('./pages/escritorio/Apuracao'));
const ApuracaoPresumido = lazy(() => import('./pages/escritorio/ApuracaoPresumido'));
const ApuracaoReal = lazy(() => import('./pages/escritorio/ApuracaoReal'));
const Projetos = lazy(() => import('./pages/escritorio/Projetos'));
const Entregas = lazy(() => import('./pages/escritorio/Entregas'));
const PainelJoao = lazy(() => import('./pages/escritorio/PainelJoao'));
const Inicio = lazy(() => import('./pages/escritorio/InicioSistema'));
const FechamentoSimples = lazy(() => import('./pages/escritorio/FechamentoSimples'));
const Prolabore = lazy(() => import('./pages/escritorio/Prolabore'));
const FolhaClt = lazy(() => import('./pages/escritorio/FolhaClt'));
const DriveClientes = lazy(() => import('./pages/escritorio/DriveClientes'));
const GestaoEquipe = lazy(() => import('./pages/escritorio/GestaoEquipe'));
const LayoutsDominio = lazy(() => import('./pages/escritorio/LayoutsDominio'));
const DashboardPerformance = lazy(() => import('./pages/escritorio/DashboardPerformance'));
const PerformanceEquipe = lazy(() => import('./pages/escritorio/PerformanceEquipe'));
const PontosBonus = lazy(() => import('./pages/escritorio/PontosBonus'));
const MeusPontos = lazy(() => import('./pages/escritorio/MeusPontos'));
const PerformanceScore = lazy(() => import('./pages/escritorio/PerformanceScore'));
const AuditLog = lazy(() => import('./pages/escritorio/AuditLog'));
const MapaSistema = lazy(() => import('./pages/escritorio/MapaSistema'));
const ControleDctf = lazy(() => import('./pages/escritorio/ControleDctf'));
const Reinf = lazy(() => import('./pages/escritorio/Reinf'));
const EfdContribuicoes = lazy(() => import('./pages/escritorio/EfdContribuicoes'));
const ControleCertificados = lazy(() => import('./pages/escritorio/ControleCertificados'));
const CertificadosNivelon = lazy(() => import('./pages/escritorio/CertificadosNivelon'));
const Societario = lazy(() => import('./pages/escritorio/Societario'));
const PainelObrigacoes = lazy(() => import('./pages/escritorio/PainelObrigacoes'));
const RelatoriosContabeis = lazy(() => import('./pages/escritorio/RelatoriosContabeis'));
// João · Balancetes (26/09/2026) — extrato → TXT Domínio + balancete PDF.
const Balancetes = lazy(() => import('./pages/escritorio/Balancetes'));
const BalanceteCliente = lazy(() => import('./pages/escritorio/BalanceteCliente'));
// Módulo contábil (13/10/2026): plano referencial da Receita, abertura de saldos, imobilizado.
const PlanoContas = lazy(() => import('./pages/escritorio/PlanoContas'));
const Imobilizado = lazy(() => import('./pages/escritorio/Imobilizado'));
const AberturaSaldos = lazy(() => import('./pages/escritorio/AberturaSaldos'));
const ContabilCliente = lazy(() => import('./pages/escritorio/ContabilCliente'));
const ContabilPainelMes = lazy(() => import('./pages/escritorio/ContabilPainelMes'));
const EmpresaFicha = lazy(() => import('./pages/escritorio/EmpresaFicha'));
const BalancetesMatriz = lazy(() => import('./pages/escritorio/BalancetesMatriz'));
const PrestacaoCosems = lazy(() => import('./pages/escritorio/PrestacaoCosems'));
const BalancetesValidacao = lazy(() => import('./pages/escritorio/BalancetesValidacao'));
const SimuladorReforma = lazy(() => import('./pages/escritorio/SimuladorReforma'));
const TransicaoClientes = lazy(() => import('./pages/escritorio/TransicaoClientes'));
const MinhaConta = lazy(() => import('./pages/escritorio/MinhaConta'));
const NotFound = lazy(() => import('./pages/NotFound'));

// Super App (PWA cliente final) — Fase 0 + Fase 1
const LayoutCliente = lazy(() => import('./components/cliente/LayoutCliente'));
const PromptBiometria = lazy(() => import('./components/cliente/PromptBiometria'));
const LoginCliente = lazy(() => import('./pages/cliente/LoginCliente'));
const TrocarSenhaCliente = lazy(() => import('./pages/cliente/TrocarSenhaCliente'));
const EsqueciSenha = lazy(() => import('./pages/cliente/EsqueciSenha'));
const HomeCliente = lazy(() => import('./pages/cliente/HomeSwitch')); // antiga ou redesenho, pelo interruptor
const MeuNegocio = lazy(() => import('./pages/cliente/MeuNegocio'));
const ContabilidadeCliente = lazy(() => import('./pages/cliente/Contabilidade'));
const NfLista = lazy(() => import('./pages/cliente/NfLista'));
const NfNova = lazy(() => import('./pages/cliente/EmitirNotaEntrada'));
const NfeNova = lazy(() => import('./pages/cliente/NfeNova'));
const NfeDetalhe = lazy(() => import('./pages/cliente/NfeDetalhe'));
const NfeCartaCorrecao = lazy(() => import('./pages/cliente/NfeCartaCorrecao'));
const NfDetalhe = lazy(() => import('./pages/cliente/NfDetalhe'));
const NfCorrigir = lazy(() => import('./pages/cliente/NfCorrigir'));
const NfLote = lazy(() => import('./pages/cliente/NfLote'));
const FalarComEquipe = lazy(() => import('./pages/cliente/FalarComEquipe'));
const ChamadosCliente = lazy(() => import('./pages/cliente/Chamados'));
const QueroSerCliente = lazy(() => import('./pages/cliente/QueroSerCliente'));
const LinkConsultor = lazy(() => import('./pages/cliente/LinkConsultor'));
const DemoAtalho = lazy(() => import('./pages/parceiros/DemoAtalho'));
const ParceirosComerciais = lazy(() => import('./pages/escritorio/ParceirosComerciais'));
const PainelParceiros = lazy(() => import('./pages/parceiros/PainelParceiros'));
const CertificadoCliente = lazy(() => import('./pages/cliente/Certificado'));
const CertificadoraSimulada = lazy(() => import('./pages/cliente/CertificadoraSimulada'));
const EntrarTesteCliente = lazy(() => import('./pages/cliente/EntrarTeste'));
const CertificadoCompraCockpit = lazy(() => import('./pages/escritorio/CertificadoCompra'));
const Impostos = lazy(() => import('./pages/cliente/Impostos'));
const Certidoes = lazy(() => import('./pages/cliente/Certidoes'));
const DeclaracoesFaturamento = lazy(() => import('./pages/cliente/DeclaracoesFaturamento'));
const ParcelamentoCliente = lazy(() => import('./pages/cliente/Parcelamento'));
const DebitosCliente = lazy(() => import('./pages/cliente/Debitos'));
const PessoaFisicaCliente = lazy(() => import('./pages/cliente/PessoaFisica'));
const MinhaEmpresa = lazy(() => import('./pages/cliente/MinhaEmpresa'));
const DocumentosCliente = lazy(() => import('./pages/cliente/Documentos'));
const EnviarParaMarcal = lazy(() => import('./pages/cliente/EnviarParaMarcal'));
const ReceberDocumento = lazy(() => import('./pages/cliente/ReceberDocumento')); // arquivo compartilhado de outro app
// [Bruno 2026-08-24] Separação por QUEM DEVE: a empresa paga o DAS e os DARF
// (Impostos); o sócio paga o INSS e o IRRF do pró-labore, no CPF dele
// (Impostos Folha). O Calendário é o único lugar que junta as duas famílias.
const ImpostosFolha = lazy(() => import('./pages/cliente/ImpostosFolha'));
const FolhaCliente = lazy(() => import('./pages/cliente/Folha'));
const FolhaRelatorios = lazy(() => import('./pages/cliente/FolhaRelatorios'));
const FolhaSolicitacoes = lazy(() => import('./pages/cliente/FolhaSolicitacoes'));
const FolhaSolicitacaoNova = lazy(() => import('./pages/cliente/FolhaSolicitacaoNova'));
const FolhaFuncionarios = lazy(() => import('./pages/cliente/FolhaFuncionarios'));
const Calendario = lazy(() => import('./pages/cliente/Calendario'));
const MeusDados = lazy(() => import('./pages/cliente/MeusDados'));
const ContadorAnteriorCliente = lazy(() => import('./pages/cliente/ContadorAnterior'));
const QuemAcessa = lazy(() => import('./pages/cliente/QuemAcessa'));
const MenuPagina = lazy(() => import('./pages/cliente/MenuPagina'));
const Notificacoes = lazy(() => import('./pages/cliente/Notificacoes'));
const Ajuda = lazy(() => import('./pages/cliente/Ajuda'));
const Privacidade = lazy(() => import('./pages/cliente/Privacidade'));
const Termos = lazy(() => import('./pages/cliente/Termos'));
const SobrePublico = lazy(() => import('./pages/cliente/Sobre').then((m) => ({ default: m.SobrePublico })));
// [Pablo 2026-10-20] White label do Meu Contador: app da marca, painel do cliente e painel da Marçal.
const EntradaMarca = lazy(() => import('./pages/whitelabel/EntradaMarca'));
const AdminMarca = lazy(() => import('./pages/whitelabel/AdminMarca'));
const PainelWhiteLabel = lazy(() => import('./pages/whitelabel/PainelWhiteLabel'));

import './styles/global.css';
import './styles/liquid-glass.css';
import './styles/cockpit-banking.css';
// Editorial: importado POR ÚLTIMO pra ter precedência no light theme.
// Aplica APENAS dentro de elementos com className="cockpit-editorial".
import './styles/cockpit-editorial.css';
// 19/06/2026: Level branding do Cockpit — aplicado em .cockpit-level
// (LayoutEscritorio). Mapeia tokens editoriais pra Level e oferece
// classes cl-* pra telas migradas.
import './styles/cockpit-level.css';
// Camada base de componentes (Toast + Confirm) — aditiva, prefixo .ui-
import './components/ui/ui.css';
// AZUL PLANTO (2026-07-11): skin glass — SEMPRE por último (vence a cascata).
// Só afeta o tema escuro; [data-theme="light"] é o rollback. Reverter = remover esta linha.
import './styles/azul-planto.css';
import './styles/tema-papel.css';
import './styles/tema-escuro.css'; // par escuro do papel (botão sol/lua, 2026-10-09)
// [Bruno 2026-08-19] Rolagem travada SÓ no Android. Congela o fundo animado,
// desliga o backdrop-filter e encurta as animações de entrada — tudo pendurado em
// html.is-android (marcado em public/index.html), então iOS e navegador não são
// tocados. Reverter = remover esta linha.
import './styles/android-performance.css';
// [Bruno 2026-09-22] Casca CRM: menu lateral de altura cheia + barra de topo.
// Escopada em html[data-ui="crm"] (liga com ?ui=crm). Reverter = remover esta linha.
import './styles/cockpit-crm.css';
// [Bruno 2026-10-02] Design system Marçal Sistema. Só casa com
// html[data-ui="sistema"] (padrão desde 02/10; quem escolheu o visual de antes
// fica sem o atributo e nenhuma regra dele se aplica).
import './styles/sistema.css';

// Evita flash de tema no primeiro paint do app do cliente (antes do React montar).
if (typeof document !== 'undefined' && typeof window !== 'undefined'
    && (window.location.pathname.startsWith('/cliente') || window.location.pathname.startsWith('/w/'))) {
  document.documentElement.setAttribute('data-theme', 'light');
}

// [Bruno 2026-10-08] A mesma tela serve cada casa pela consulta (?casa=,
// ?origem=). Trocar de item no menu muda só a consulta — sem remontar, a tela
// ficaria presa no filtro da casa anterior. A `key` pela consulta remonta.
// [Bruno 2026-10-13] O WhatsApp abre sempre numa casa: link antigo sem ?casa=
// cai no Meu Contador (o menu acende no item certo).
function WhatsAppComCasa() {
  const { search } = useLocation();
  const casa = new URLSearchParams(search).get('casa');
  if (casa !== 'meu_contador' && casa !== 'nivelon') {
    const p = new URLSearchParams(search);
    p.set('casa', 'meu_contador');
    return <Navigate to={`/escritorio/whatsapp?${p.toString()}`} replace />;
  }
  return <PorConsulta><WhatsAppPainel /></PorConsulta>;
}

function PorConsulta({ children }) {
  const { search } = useLocation();
  return <React.Fragment key={search}>{children}</React.Fragment>;
}

// Troca o tema por rota em navegações SPA: /cliente = claro, resto = escuro.
// O painel das marcas mora dentro do cockpit; o endereço antigo leva até lá (com a busca).
/* A tela antiga só da NivelOn (/escritorio/nivelon) virou a lista única de
   chamados com a empresa no filtro (Pablo, 23/10 e 27/10): o link antigo
   leva pra lá, mantendo ?chamado=ID. */
function RedirecionaChamadosNivelon() {
  const { search } = useLocation();
  const p = new URLSearchParams(search);
  p.set('fila', 'nivelon');
  return <Navigate to={`/escritorio/chamados?${p.toString()}`} replace />;
}

function RedirecionaWhiteLabel() {
  const { search } = useLocation();
  return <Navigate to={`/escritorio/white-label${search}`} replace />;
}

function AplicarTema() {
  const { pathname } = useLocation();
  const marca = useMarca();
  useEffect(() => {
    // /w/<marca> (entrada e painel do cliente da marca) é claro como o app: com "dark" o
    // liquid-glass pintava campo e cabeçalho de tabela de azul-marinho por cima do painel.
    const claro = pathname.startsWith('/cliente') || pathname.startsWith('/w/');
    document.documentElement.setAttribute('data-theme', claro ? 'light' : 'dark');
    // App de marca (white label): só nas telas do app do cliente.
    aplicarNoDocumento(pathname.startsWith('/cliente') ? marca : null);
  }, [pathname, marca]);
  // App de marca: nada da Marçal na tela (rede que pega texto de tela, do servidor e de aviso).
  const noApp = pathname.startsWith('/cliente') || (pathname.startsWith('/w/') && !/^\/w\/[^/]+\/admin/.test(pathname));
  useEffect(() => {
    if (!noApp || !marca) return undefined;
    return vigiarSemMarcal(document.body, marca);
  }, [noApp, marca]);
  return null;
}

// Fallback enquanto o chunk carrega — spinner discreto (o que apps usam),
// sem "ampulheta". Em geral o fetch é < 200ms com o chunk em cache.
function LoadingPagina() {
  return (
    <div style={{ padding: '72px 20px', display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 12 }}>
      <span style={{
        width: 30, height: 30, borderRadius: '50%',
        border: '3px solid rgba(27,58,107,0.15)', borderTopColor: '#1B3A6B',
        animation: 'sa-girar 0.7s linear infinite',
      }} />
      <div style={{ fontSize: 14, color: 'var(--sa-ink-soft, #6E6A5E)' }}>Carregando…</div>
    </div>
  );
}

// Rota protegida
/**
 * [Bruno 2026-08-26] O parceiro do societário (a Cristine) entra no MESMO
 * cockpit, mas só alcança a fila de chamados dele. O backend já recusa o resto
 * — esta é a camada que evita ele BATER na recusa: sem isto, ao logar cairia na
 * Bandeja e veria uma tela de erro, e cada item do menu daria 403.
 *
 * A trava de verdade continua sendo a do servidor. Esta aqui é de UX: manda pra
 * casa dele em vez de mostrar porta trancada.
 */
export function ehParceiroSocietario(usuario) {
  return !!usuario && usuario.papel === 'parceiro_societario';
}

const ROTA_DO_PARCEIRO = '/escritorio/chamados';
// [Bruno 2026-09-13] O departamento pessoal (a Deborah) tem a mesma casa, na aba
// da folha. A trava de verdade é do servidor (papel negado fora dos chamados).
export function ehDepartamentoPessoal(usuario) {
  return !!usuario && usuario.papel === 'departamento_pessoal';
}
const ROTA_DO_DP = '/escritorio/chamados?fila=folha';

function RotaProtegida({ children }) {
  const { usuario, carregando } = useAuth();
  const local = useLocation();

  if (carregando) return <div style={{ padding: 40 }}>Carregando...</div>;
  if (!usuario) return <Navigate to="/login" />;

  // Fora da fila dele, volta pra ela. Vale pro endereço digitado à mão também —
  // é o roteador que decide, não o menu.
  if (ehParceiroSocietario(usuario) && !local.pathname.startsWith(ROTA_DO_PARCEIRO)) {
    return <Navigate to={ROTA_DO_PARCEIRO} replace />;
  }
  // O DP também fecha a folha de funcionários (o servidor libera /api/folha-clt pra ele).
  // O chat da equipe também é do DP (é equipe interna).
  if (ehDepartamentoPessoal(usuario) && !local.pathname.startsWith('/escritorio/chat') && !local.pathname.startsWith('/escritorio/agenda') && !local.pathname.startsWith(ROTA_DO_PARCEIRO) && !local.pathname.startsWith('/escritorio/folha-clt')) {
    return <Navigate to={ROTA_DO_DP} replace />;
  }

  // [Pablo 2026-10-22] O login do Primeiro contato (a Onésia) fica só no módulo dela
  // (e em Minha conta, para trocar a senha e sair). A trava de verdade é do servidor.
  if (usuario.papel === 'primeiro_contato' && !local.pathname.startsWith('/escritorio/primeiro-contato') && !local.pathname.startsWith('/escritorio/minha-conta')) {
    return <Navigate to="/escritorio/primeiro-contato?casa=nivelon" replace />;
  }

  return children;
}

// Rota cliente protegida — splash editorial enquanto valida sessão.
function RotaClienteProtegida({ children }) {
  const { autenticado, carregando, bloqueadoPorBiometria, desbloquearBiometria, logout } = useClienteAuth();
  const marcaSplash = useMarca();
  if (carregando) {
    return (
      <div style={{
        minHeight: '100vh',
        background: marcaSplash ? marcaSplash.tema.fundo : 'linear-gradient(180deg, #0a1f3d 0%, #143764 100%)',
        display: 'flex', flexDirection: 'column',
        alignItems: 'center', justifyContent: 'center',
        color: marcaSplash ? marcaSplash.tema.texto : '#fff', fontFamily: 'var(--sa-display, "Plus Jakarta Sans", sans-serif)',
      }}>
        <div style={{ fontSize: 28, fontWeight: 600, letterSpacing: '-0.02em' }}>{marcaSplash ? marcaSplash.nome_app : 'Marçal'}</div>
        {!marcaSplash && <div style={{
          fontSize: 11, letterSpacing: '0.18em', textTransform: 'uppercase',
          opacity: 0.7, marginTop: 4,
        }}>Meu Contador</div>}
        <div style={{ marginTop: 32, fontSize: 12, opacity: 0.5 }}>Conectando…</div>
      </div>
    );
  }
  if (!autenticado) return <Navigate to="/cliente/login" replace />;
  if (bloqueadoPorBiometria) {
    return (
      <PromptBiometria
        onDesbloquear={desbloquearBiometria}
        onCancelar={() => logout()}
      />
    );
  }
  return children;
}

function App() {
  return (
    <BrowserRouter>
      <ThemeProvider>
      <UIProvider>
      <AuthProvider>
      <ClienteAuthProvider>
        <AplicarTema />
        <LimiteDeErro>
        <Suspense fallback={<LoadingPagina />}>
        <Routes>
          {/* Login */}
          <Route path="/login" element={<Login />} />
          {/* TV de atendimento: endereço curto + login próprio (só leitura) */}
          <Route path="/tv" element={<TvPublica />} />
          <Route path="/enviar-certificado/:token" element={<EnviarCertificado />} />
          <Route path="/certificadora-simulada" element={<CertificadoraSimulada publico />} />
          <Route path="/enviar-senha/:token" element={<EnviarSenha />} />
          <Route path="/avaliar/:token" element={<AvaliarAtendimento />} />
          <Route path="/certificado" element={<CertificadoCodigo />} />
          {/* Atendimento no celular (13/10/2026): só o CRM de uma casa, sem o menu do cockpit. */}
          <Route path="/atendimento/:slug" element={<AtendimentoMovel />} />
          <Route path="/atendimento" element={<AtendimentoMovel />} />

          {/* Raiz → home operacional */}
          {/* 23/08/2026 — a BANDEJA virou a porta de entrada (decisão do
              Thiago). A home deixou de ser um painel do que existe e virou a
              fila do que precisa ser feito. "Hoje" continua em Início › Hoje. */}
          <Route path="/" element={<Navigate to="/escritorio/inicio" />} />

          {/* Painel do Escritório */}
          <Route path="/escritorio" element={
            <RotaProtegida>
              <LayoutEscritorio />
            </RotaProtegida>
          }>
            {/* Sprint 5.12: /escritorio (sem path) redireciona pra Home v5.
                Antes carregava o "Painel do Escritório" antigo — era o que o
                celular do Thiago abria por bookmark/historico antigo. */}
            <Route index element={<Navigate to="inicio" replace />} />
            <Route path="painel-gerencial" element={<Navigate to="/escritorio/inicio" replace />} />
            <Route path="avisos-clientes" element={<AvisosClientes />} />
            <Route path="aviso-ios27" element={<AvisoIos27 />} />
            {/* [25/08/2026] O "Painel clássico" foi removido a pedido do Bruno — o
                Painel gerencial o substituiu. A rota continua existindo como
                REDIRECIONAMENTO porque ela era bookmark de gente da equipe: link
                antigo tem que cair em algum lugar útil, não em tela branca. */}
            <Route path="dashboard-classico" element={<Navigate to="/escritorio/inicio" replace />} />
            <Route path="operacoes" element={<Navigate to="/escritorio/inicio" replace />} />
            <Route path="fila-ana" element={<FilaAprovacaoAna />} />
            <Route path="apuracao" element={<Apuracao />} />
            <Route path="inicio" element={<Inicio />} />
            <Route path="apuracao-modulo" element={<Navigate to="/escritorio/fechamento-simples" replace />} />
            <Route path="apuracao-presumido" element={<ApuracaoPresumido />} />
            <Route path="apuracao-real" element={<ApuracaoReal />} />
            <Route path="projetos" element={<Projetos />} />
            {/* [2026-10-20] Telas aposentadas levam à que as substituiu (endereço salvo
                em favorito não vira 404). O controle antigo do Firebase saiu: o
                Controle fiscal integrado tem o preenchimento à mão. */}
            <Route path="entregas" element={<Navigate to="/escritorio/controle-fiscal" replace />} />
            <Route path="entregas-serpro" element={<Entregas />} />
            <Route path="aprovacoes" element={<Aprovacoes />} />
            <Route path="ultimas-emitidas" element={<UltimasEmitidas />} />
            {/* [Bruno 2026-10-08] /notas virou o Controle de Notas (cliente → mês → notas). */}
            <Route path="notas" element={<ControleNotas />} />
            <Route path="todas-notas" element={<Navigate to="/escritorio/notas" replace />} />
            <Route path="notas/draft/:id" element={<RascunhoNF />} />
            {/* /notas/:id: detalhe de NF (emitida/cancelada/rejeitada). Reusa RascunhoNF
                em modo read-only — ele já renderiza banner "esta NF não está em rascunho"
                quando o status não é rascunho. Antes deste fix, o feed "Últimas NFs" navegava
                pra /notas/:id mas a rota nem existia → catch-all silenciava no dashboard. */}
            <Route path="notas/:id" element={<RascunhoNF />} />
            <Route path="reports" element={<BugReports />} />
            <Route path="drive-clientes" element={<DriveClientes />} />
            <Route path="pgdasd-corrida" element={<Navigate to="/escritorio/fechamento-simples" replace />} />
            <Route path="equipe" element={<GestaoEquipe />} />
            <Route path="layouts-dominio" element={<LayoutsDominio />} />
            <Route path="performance" element={<DashboardPerformance />} />
            {/* Performance da EQUIPE (score por pessoa) — rota própria porque
                /performance já era o dashboard técnico de rotinas + João.
                O acesso é travado no backend por e-mail nominal; a tela mostra
                só o próprio score pra quem não está na lista. */}
            <Route path="performance-equipe" element={<PerformanceEquipe />} />
            <Route path="performance-score" element={<PerformanceScore />} />
            <Route path="pontos-bonus" element={<PontosBonus />} />
            <Route path="meus-pontos" element={<MeusPontos />} />
            <Route path="audit" element={<AuditLog />} />
            <Route path="minha-conta" element={<MinhaConta />} />
            <Route path="clientes" element={<PorConsulta><GestaoClientes /></PorConsulta>} />
            <Route path="contratos" element={<Contratos />} />
            <Route path="financeiro" element={<Financeiro />} />
            <Route path="emitir-nota" element={<EmitirNota />} />
            <Route path="emitir" element={<EmitirNFEscritorio />} />
            <Route path="emitir-lote" element={<EmitirLote />} />
            <Route path="nfe-produto" element={<NfeProduto />} />
            <Route path="nfe-credenciamento" element={<NfeCredenciamento />} />
            <Route path="declaracao" element={<EmitirDeclaracao />} />
            <Route path="certidao" element={<EmitirCertidao />} />
            <Route path="certidoes-lista" element={<CertidoesLista />} />
            <Route path="acesso-empresas" element={<AcessoEmpresas />} />
            <Route path="folha-12m" element={<Folha12m />} />
            <Route path="saude-crons" element={<SaudeCrons />} />
            {/* [Bruno 2026-10-14] Painel da Julia: manutenção do sistema (só leitura). */}
            <Route path="manutencao" element={<Manutencao />} />
            <Route path="acessos" element={<Acessos />} />
            <Route path="white-label" element={<PainelWhiteLabel />} />
            <Route path="parceiros-comerciais" element={<ParceirosComerciais />} />
            <Route path="julia-manutencao" element={<Navigate to="/escritorio/manutencao" replace />} />
            <Route path="consumo-serpro" element={<ConsumoSerpro />} />
            <Route path="procuracoes-ecac" element={<ProcuracoesEcac />} />
            <Route path="motor-nf" element={<MotorNf />} />
            <Route path="motor-nf/:id" element={<MotorNfDiagnostico />} />
            <Route path="ana-metricas" element={<AnaMetricas />} />
            <Route path="cliente-espelho" element={<ClienteEspelho />} />
            <Route path="observabilidade" element={<Observabilidade />} />
            <Route path="cliente-erros" element={<ClienteErros />} />
            <Route path="guias-drive-nomenclatura" element={<GuiasDriveNomenclatura />} />
            <Route path="mapa" element={<MapaSistema />} />
            <Route path="dctf" element={<ControleDctf />} />
            <Route path="reinf" element={<Reinf />} />
            <Route path="efd-contribuicoes" element={<EfdContribuicoes />} />
            <Route path="painel-obrigacoes" element={<PainelObrigacoes />} />
            {/* [Bruno 2026-10-09] Controle Fiscal integrado: uma linha por empresa, por competência. */}
            <Route path="controle-fiscal" element={<ControleFiscalMensal />} />
            <Route path="relatorios-contabeis" element={<RelatoriosContabeis />} />
            <Route path="balancetes" element={<Balancetes />} />
            <Route path="plano-contas" element={<PlanoContas />} />
            <Route path="imobilizado" element={<Imobilizado />} />
            <Route path="contabil" element={<ContabilCliente />} />
            <Route path="contabil/painel" element={<ContabilPainelMes />} />
            <Route path="empresa" element={<EmpresaFicha />} />
            <Route path="contabil/abertura/:clienteId/:competencia" element={<AberturaSaldos />} />
            <Route path="balancetes-status" element={<BalancetesMatriz />} />
            <Route path="prestacao-cosems" element={<PrestacaoCosems />} />
            <Route path="prestacao-cosems/:clienteId/:quad" element={<PrestacaoCosems />} />
            <Route path="balancetes-validacao" element={<BalancetesValidacao />} />
            <Route path="balancetes/:clienteId/:competencia" element={<BalanceteCliente />} />
            <Route path="simulador-reforma" element={<SimuladorReforma />} />
            <Route path="transicao-clientes" element={<TransicaoClientes />} />
            <Route path="documentos" element={<PorConsulta><CentralDocumentos /></PorConsulta>} />
            {/* Tela avulsa de A1 aposentada (2026-07-21): o envio de arquivo virou
                modal dentro do controle. Redireciona pra não quebrar link antigo. */}
            <Route path="certificados" element={<Navigate to="/escritorio/controle-certificados" replace />} />
            <Route path="controle-certificados" element={<ControleCertificados />} />
            <Route path="certificados-nivelon" element={<CertificadosNivelon />} />
            <Route path="certificado-compra" element={<CertificadoCompraCockpit />} />
            <Route path="societario" element={<Societario />} />
            {/* Licenças virou aba do hub Societário — redireciona link antigo */}
            <Route path="controle-licencas" element={<Navigate to="/escritorio/societario?view=licencas" replace />} />
            <Route path="certificado-serpro" element={<CertificadoSerpro />} />
            <Route path="integra-contador" element={<IntegraContador />} />
            <Route path="dominio" element={<ApiDominio />} />
            <Route path="whatsapp" element={<WhatsAppComCasa />} />
            <Route path="primeiro-contato" element={<PorConsulta><PrimeiroContatoPainel /></PorConsulta>} />
            <Route path="chamados" element={<ChamadosPainel key="chamados" />} />
            <Route path="chat" element={<ChatEquipe />} />
            <Route path="agenda" element={<Agenda />} />
            {/* Painel da TV do atendimento (07/10/2026): cobre a tela inteira por cima do menu. */}
            <Route path="tv-atendimento" element={<PorConsulta><TvAtendimento /></PorConsulta>} />
            <Route path="tv-empresas" element={<PorConsulta><TvEmpresas /></PorConsulta>} />
            <Route path="central-atendimento" element={<PorConsulta><CentralAtendimento /></PorConsulta>} />
            {/* Aba NivelOn (14/09/2026): a mesma tela de chamados, recortada
                pros clientes que vieram pelo app — ver Sidebar › NivelOn. A `key`
                força remontar ao trocar de aba (mesmo componente, rota diferente). */}
            <Route path="nivelon" element={<RedirecionaChamadosNivelon />} />
            <Route path="cadastros" element={<PorConsulta><Cadastros /></PorConsulta>} />
            <Route path="pendencias-cadastrais" element={<PorConsulta><PendenciasCadastrais /></PorConsulta>} />
            <Route path="nivelon-whatsapp" element={<WhatsappNivelonConexao />} />
            <Route path="nivelon-painel" element={<PainelNivelon />} />
            <Route path="nivelon-contas" element={<ContasNivelon />} />
            <Route path="whatsapp-importar" element={<WhatsappImportar />} />
            {/* Pastas fundida na Central de Documentos (07/09/2026) — link antigo redireciona */}
            <Route path="pastas" element={<Navigate to="/escritorio/documentos" replace />} />
            <Route path="bandeja" element={<Navigate to="/escritorio/chamados" replace />} />
            <Route path="crm" element={<Navigate to="/escritorio/chamados" replace />} />
            <Route path="ana" element={<PainelAna />} />
            <Route path="ana-triagem" element={<AnaTriagem />} />
            <Route path="ana-fluxo" element={<FluxoAtendimento />} />
            <Route path="ana-conhecimento" element={<AnaConhecimento />} />
            <Route path="ana-casa" element={<AnaConhecimento modo="casa" />} />
            <Route path="ana-desempenho" element={<AnaDesempenho />} />
            <Route path="ana-simulacao" element={<AnaSimulacao />} />
            <Route path="jornada-clientes" element={<JornadaClientes />} />
            <Route path="aparelhos-clientes" element={<AparelhosClientes />} />
            <Route path="relatorios" element={<Relatorios />} />
            <Route path="testar-agente" element={<TestarAgente />} />
            <Route path="leticia-chamados" element={<LeticiaChamados />} />
            <Route path="joao" element={<PainelJoao />} />
            <Route path="fechamento-simples" element={<FechamentoSimples />} />
            <Route path="prolabore" element={<Prolabore />} />
            <Route path="folha-clt" element={<FolhaClt />} />
          </Route>

          {/* Super App PWA (cliente final) — rota /cliente fora do LayoutEscritorio.
              Layout próprio: sem Navbar de operador, foco em UX mobile-first.
              Privacidade/termos: PÚBLICAS sem auth (Apple e Google revisam). */}
          <Route path="/cliente/privacidade" element={<Privacidade />} />
          <Route path="/cliente/termos" element={<Termos />} />
          {/* [Bruno 2026-09-28] O "Sobre" do rodapé do login levava pro login de novo. */}
          <Route path="/cliente/sobre" element={<SobrePublico />} />
          <Route path="/cliente/login" element={<LoginCliente />} />
          <Route path="/w/:slug" element={<EntradaMarca />} />
          <Route path="/w/:slug/admin/*" element={<AdminMarca />} />
          <Route path="/white-label/*" element={<RedirecionaWhiteLabel />} />
          {/* Celular de teste do cockpit (certificado pela certificadora). */}
          <Route path="/cliente/entrar-teste" element={<EntrarTesteCliente />} />
          <Route path="/cliente/esqueci-senha" element={<EsqueciSenha />} />
          {/* Porta pública: quem AINDA não é cliente. Não cria cadastro — abre um
              pedido que a Marçal aprova (ver services/cadastroSolicitacaoService.js). */}
          <Route path="/cliente/quero-ser-cliente" element={<QueroSerCliente />} />
          {/* [Thiago 2026-10-30] Link do consultor parceiro e o painel do time (login próprio). */}
          <Route path="/c/:codigo" element={<LinkConsultor />} />
          <Route path="/demo/:chave" element={<DemoAtalho />} />
          <Route path="/parceiros/*" element={<PainelParceiros />} />
          <Route path="/cliente" element={
            <RotaClienteProtegida>
              <LayoutCliente />
            </RotaClienteProtegida>
          }>
            <Route index element={<HomeCliente />} />
            <Route path="trocar-senha" element={<TrocarSenhaCliente />} />
            <Route path="nf" element={<NfLista />} />
            <Route path="nf/nova" element={<NfNova />} />
            <Route path="nf/lote" element={<NfLote />} />
            <Route path="nf/:id" element={<NfDetalhe />} />
            <Route path="nf/:id/corrigir" element={<NfCorrigir />} />
            <Route path="nfe/nova" element={<NfeNova />} />
            <Route path="nfe/:id" element={<NfeDetalhe />} />
            <Route path="nfe/:id/correcao" element={<NfeCartaCorrecao />} />
            <Route path="equipe" element={<FalarComEquipe />} />
            <Route path="chamados" element={<ChamadosCliente />} />
            <Route path="impostos" element={<Impostos />} />
            <Route path="impostos-folha" element={<ImpostosFolha />} />
            <Route path="folha" element={<FolhaCliente />} />
            <Route path="folha/relatorios" element={<FolhaRelatorios />} />
            <Route path="folha/solicitacoes" element={<FolhaSolicitacoes />} />
            <Route path="folha/solicitacoes/nova/:tipo" element={<FolhaSolicitacaoNova />} />
            <Route path="folha/funcionarios" element={<FolhaFuncionarios />} />
            <Route path="calendario" element={<Calendario />} />
            <Route path="certidoes" element={<Certidoes />} />
            <Route path="declaracoes" element={<DeclaracoesFaturamento />} />
            <Route path="parcelamento" element={<ParcelamentoCliente />} />
            <Route path="debitos" element={<DebitosCliente />} />
            <Route path="pessoa-fisica" element={<PessoaFisicaCliente />} />
            <Route path="empresa" element={<MinhaEmpresa />} />
            <Route path="documentos" element={<DocumentosCliente />} />
            <Route path="documentos/receber" element={<ReceberDocumento />} />
            <Route path="documentos/enviar" element={<EnviarParaMarcal />} />
            <Route path="certificado" element={<CertificadoCliente />} />
            <Route path="certificado/certificadora-simulada" element={<CertificadoraSimulada />} />
            <Route path="meus-dados" element={<MeusDados />} />
            <Route path="contador-anterior" element={<ContadorAnteriorCliente />} />
            <Route path="quem-acessa" element={<QuemAcessa />} />
            <Route path="menu" element={<MenuPagina />} />
            <Route path="meu-negocio" element={<MeuNegocio />} />
            <Route path="contabilidade" element={<ContabilidadeCliente />} />
            <Route path="notificacoes" element={<Notificacoes />} />
            <Route path="ajuda" element={<Ajuda />} />
          </Route>

          {/* 404 — antes era <Navigate to="/" /> que silenciava URLs inválidas. */}
          <Route path="*" element={<NotFound />} />
        </Routes>
        </Suspense>
        </LimiteDeErro>
      </ClienteAuthProvider>
      </AuthProvider>
      </UIProvider>
      </ThemeProvider>
    </BrowserRouter>
  );
}

export default App;
